import React, { useEffect, useState } from 'react';
import { settingsService } from 'services/firestore';
import { logger } from 'utils/logger';
import 'styles/components/announcement-bar.css';

// STORY-03.1.03 \u2014 the storefront announcement bar is now driven by the
// operator-facing settings/store doc (toggled + edited under /admin/settings).
// Legacy site_settings/topbar is no longer the source; ops can leave it in
// place but the storefront ignores it.
//
// The dismissal-for-7-days UX is preserved \u2014 it's a customer-side comfort,
// not an operator concern. If the operator edits the text in admin, the
// dismissal key invalidates because we incorporate the text hash into it,
// so customers see the NEW message even if they previously dismissed.

const DISMISS_KEY_PREFIX = 'cl_ann_dismissed_v2:';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in ms

// Tiny stable hash of the announcement text so a re-edit pops the bar back
// for customers who previously dismissed. djb2 \u2014 good enough for cache-bust.
const hashText = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

const AnnouncementBar = () => {
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await settingsService.get('store');
        if (cancelled) return;
        setSettings(data);

        if (data?.announcement_active && data?.announcement_text) {
          const dismissKey = DISMISS_KEY_PREFIX + hashText(data.announcement_text);
          const dismissedAt = localStorage.getItem(dismissKey);
          if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION) {
            setDismissed(true);
          }
        }
      } catch (error) {
        logger.error('Error fetching announcement settings:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    if (settings?.announcement_text) {
      const dismissKey = DISMISS_KEY_PREFIX + hashText(settings.announcement_text);
      localStorage.setItem(dismissKey, Date.now().toString());
    }
  };

  if (loading || dismissed) return null;
  if (!settings?.announcement_active) return null;
  if (!settings?.announcement_text?.trim()) return null;

  return (
    <div className="announcement-bar" role="complementary">
      <p className="announcement-bar__text">{settings.announcement_text}</p>
      <button
        type="button"
        className="announcement-bar__dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M2 2l8 8M10 2l-8 8" />
        </svg>
      </button>
    </div>
  );
};

export default AnnouncementBar;
